'use client';
import { ChangeEvent, useEffect, useState } from 'react';
import {
  ReadonlyURLSearchParams,
  useParams,
  usePathname,
  useSearchParams,
  useRouter,
} from 'next/navigation';
import Button from '@ui/Button';
import { extractCityNameFromUrl } from '@/lib/utils';
import { useTranslations } from 'next-intl';
import { STOREFRONT } from '@/lib/utils/translation';
import SearchOptionButtons from './SearchOptionButtons';
import { SelectOptionType } from '@/lib/types/BaseType';
import SelectWithRemoteSearch from '../shared/SelectWithRemoteSearch';
import { childTagSwiperID, env, parentTagSwiperID } from '@/constant/configs';
import { useRestaurantTagsDataStore } from '@/stores/cutomer/restaurantTags';
import TagsCarousel from '../TagsCarousel';
import ViewAllTagsModal from './ViewAllTagsModal';
import useRestaurantTags from '@/hooks/useRestaurantTags';
import { RestaurantTagsResponse } from '@/lib/types/customer/RestaurantTags';

export type SearchTypes = 'delivery' | 'pickup' | 'all';
export type VendorTypes = 'restaurant' | 'online_shop';
type Props = {
  shippingType: SearchTypes;
  vendorType?: VendorTypes;
  tagList?: RestaurantTagsResponse;
};

const initialSelectOption: SelectOptionType = { id: -1, name: '' };
export default function SearchLayoutCommon({
  shippingType,
  vendorType,
  tagList,
}: Props) {
  const t = useTranslations();
  const pathname = usePathname();
  const router = useRouter();
  const queryParams = useParams();
  const { replace } = router;
  const searchParams: ReadonlyURLSearchParams = useSearchParams();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCityOption, setSelectedCityOption] =
    useState<SelectOptionType>(initialSelectOption);

  const { searchWithChildTag, searchWithParentTag } = useRestaurantTags();

  const tagsData = useRestaurantTagsDataStore((state) => state.tagsData);
  const setRestaurantTagsData = useRestaurantTagsDataStore(
    (state) => state.setRestaurantTagsData
  );

  const setSelectedTag = useRestaurantTagsDataStore(
    (state) => state.setSelectedTag
  );
  const showChildrenTag = useRestaurantTagsDataStore(
    (state) => state.showChildrenTag
  );
  const childrenTags = useRestaurantTagsDataStore(
    (state) => state.childrenTags
  );
  const setChildrenTags = useRestaurantTagsDataStore(
    (state) => state.setChildrenTags
  );
  const setSelectedChildTags = useRestaurantTagsDataStore(
    (state) => state.setSelectedChildTags
  );
  const showViewAllTag = useRestaurantTagsDataStore(
    (state) => state.showViewAllTag
  );
  const setShowViewAllTag = useRestaurantTagsDataStore(
    (state) => state.setShowViewAllTag
  );
  const isChildTag = useRestaurantTagsDataStore((state) => state.isChildTag);

  const setSelectedParentTag = useRestaurantTagsDataStore(
    (state) => state.setSelectedParentTag
  );

  const setSelectedSearchableTags = useRestaurantTagsDataStore(
    (state) => state.setSelectedSearchableTags
  );
  const isTagsLoading = useRestaurantTagsDataStore(
    (state) => state.isTagsLoading
  );

  const resetStoreState = useRestaurantTagsDataStore(
    (state) => state.resetStoreState
  );

  useEffect(() => {
    const city = queryParams?.slug
      ? (queryParams?.slug as string)?.charAt(0).toUpperCase() +
        (queryParams?.slug as string).slice(1).toLowerCase()
      : '';
    setSelectedCityOption({ id: -1, name: city });
  }, [queryParams]);

  useEffect(() => {
    if (tagList && !tagsData) {
      setRestaurantTagsData(tagList, vendorType);
    }
  }, [tagList, tagsData, setRestaurantTagsData]);

  useEffect(() => {
    return () => {
      resetStoreState();
    };
  }, []);

  // const fetchData = () => {
  //   const params = window.location.search.substring(1);

  //   const urlParams = new URLSearchParams(params);
  //   let paramsJson: any = {};

  //   if (urlParams.size > 0) {
  //     paramsJson = Object.fromEntries(urlParams);
  //   }

  //   setLoading();
  //   search(paramsJson).then((res) => {
  //     setData(res.data);
  //   });
  // };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);

    if (searchQuery) {
      setSelectedTag(null);
      setSelectedParentTag();
      setSelectedSearchableTags();
      setSelectedChildTags();
      setChildrenTags([]);
      params.delete('tag_ids');
      params.set('search', searchQuery);
    } else {
      params.delete('search');
    }
    replace(`${pathname}?${params.toString()}`);
  };

  const changeDeliveryType = (type: SearchTypes) => {
    if (type === shippingType) {
      return;
    }
    const params = new URLSearchParams(searchParams);
    if (type === 'all') {
      params.delete('type');
    } else {
      params.set('type', type);
    }
    params.delete('free_delivery');
    const city = extractCityNameFromUrl(pathname);
    if (city) {
      params.set('city', city);
    }
    replace(`${pathname}?${params.toString()}`);
  };

  const handleKeywordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    if (e.target.value === '') {
      const params = new URLSearchParams(searchParams);
      params.delete('search');
      replace(`${pathname}?${params.toString()}`);
    }
  };

  const handleCitySelect = (option: SelectOptionType) => {
    setSelectedCityOption(option);
    router.push(`/city/${option.slug}`);
  };

  return (
    <div className="search-area">
      <div className="container">
        <div className="mx-auto max-w-[869px] pb-[80px] pt-[30px] lg:pb-[103px] lg:pt-[123px]">
          <h1 className="px-10 text-center text-[50px]/[1.3] font-[900] lg:px-0 lg:text-h1">
            {t(`${STOREFRONT.SEARCH}.header`)}
          </h1>
          {/* <p className="mb-[20px] mt-[46px] text-center text-lg text-paragraph lg:hidden">
            For Delivery and Pickup
          </p> */}
          <form
            onSubmit={handleSearch}
            className="mt-[46px] flex flex-col items-center justify-center md:mt-[60px] md:flex-row"
          >
            <input
              name="keyword"
              type="search"
              placeholder={t(`${STOREFRONT.SEARCH}.placeholder`)}
              aria-label={t(`${STOREFRONT.SEARCH}.placeholder`)}
              defaultValue={searchParams.get('search')?.toString()}
              className="searchPageSearch mb-[48px] mr-0 h-[66px] w-full rounded-full border-0 bg-white px-7 py-[21px] shadow-[0px_14px_70px_rgba(0,0,0,0.1)] outline-0 focus:border-0 focus:ring-0 md:mb-0 md:mr-5 md:w-[450px] lg:w-[503px]"
              onChange={handleKeywordChange}
            />

            <Button
              rounded
              type="submit"
              className="rounded-full border-2 border-primary-700 px-[47px] py-[21px] text-h5 font-[500] text-white transition-colors hover:bg-white hover:text-primary-700"
            >
              {t(`${STOREFRONT.SEARCH}.button_text`)}
            </Button>
          </form>
        </div>
        {isTagsLoading ? (
          <div className="mb-20 flex items-center justify-center">
            <div className="mx-auto w-full rounded-md border border-gray-100 p-4 shadow-sm">
              <div className="flex animate-pulse space-x-4">
                <div className="flex-1 space-y-6 py-1">
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="col-span-1 h-20 rounded bg-slate-200"></div>
                      <div className="col-span-1 h-20 rounded bg-slate-200"></div>
                      <div className="col-span-1 h-20 rounded bg-slate-200"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          tagsData &&
          tagsData?.data?.length > 0 && (
            <div className="mb-20">
              <TagsCarousel
                tagsResData={tagsData}
                tagsData={tagsData.data}
                handleTag={(tag) => {
                  searchWithParentTag(tag);
                }}
                carouselId={parentTagSwiperID}
              />
              {showChildrenTag && childrenTags && childrenTags?.length > 0 && (
                <TagsCarousel
                  tagsResData={tagsData}
                  tagsData={
                    childrenTags.length <= 15
                      ? childrenTags
                      : childrenTags.slice(0, 15)
                  }
                  handleTag={(tag) => {
                    setSelectedTag(tag);
                    setSelectedSearchableTags(tag);
                    searchWithChildTag({ tag, vendorType });
                  }}
                  isChildTagCarousel={true}
                  carouselId={childTagSwiperID}
                />
              )}
            </div>
          )
        )}
        {vendorType !== 'online_shop' && (
          <div className="flex flex-col items-start justify-between gap-y-4 sm:flex-row sm:items-center sm:gap-0">
            <SearchOptionButtons
              shippingType={shippingType}
              changeDeliveryType={changeDeliveryType}
            />

            <div className="flex items-center space-x-4">
              {/* <FreeDeliveryFilter shippingType={shippingType} /> */}
              {pathname?.includes('/city') && (
                <SelectWithRemoteSearch
                  apiEndpoint={env.api_url + '/v1/customer/cities'}
                  selectedOption={selectedCityOption}
                  onSelect={handleCitySelect}
                  paramKey="name"
                  placeholder={t('common.search_city')}
                  initalParams={{
                    is_favourite: 0,
                  }}
                  extraParams={{
                    order_by: 'order_index',
                    order_dir: 'asc',
                    per_page: 20,
                  }}
                />
              )}
            </div>
          </div>
        )}
      </div>
      <ViewAllTagsModal
        showViewAllTag={showViewAllTag}
        setShowViewAllTag={setShowViewAllTag}
        tagsData={isChildTag ? childrenTags : tagsData?.data}
        vendorType={vendorType}
      />
    </div>
  );
}
